import { useState } from 'react';
import { FileText, Link2, Unlink, ExternalLink, FolderOpen, Plus, Search, X, Globe, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  useDeliverableDocuments,
  useLinkDocument,
  useUnlinkDocument,
} from '@/hooks/useDeliverableDocuments';
import { useDocuments, useCreateLinkDocument } from '@/hooks/useDocuments';
import type { LinkedDocument } from '@/hooks/useDeliverableDocuments';

function timeAgo(timestamp: string): string {
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now.getTime() - then.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffDays > 0) return `${diffDays}d ago`;
  if (diffHours > 0) return `${diffHours}h ago`;
  return 'just now';
}

interface DeliverableDocumentListProps {
  deliverableId: string;
}

export function DeliverableDocumentList({ deliverableId }: DeliverableDocumentListProps) {
  const { data: linkedDocs, isLoading } = useDeliverableDocuments(deliverableId);
  const linkDocument = useLinkDocument();
  const unlinkDocument = useUnlinkDocument();
  const createLinkDocument = useCreateLinkDocument();
  const [showPicker, setShowPicker] = useState(false);
  const [showNewLink, setShowNewLink] = useState(false);
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [newLinkTitle, setNewLinkTitle] = useState('');
  const [search, setSearch] = useState('');

  // Fetch all documents for the picker (always fetch - lightweight query, avoids loading flash)
  const { data: allDocuments } = useDocuments({});

  const linkedIds = new Set((linkedDocs || []).map((d) => d.id));

  // Filter available documents (not already linked, matching search)
  const availableDocs = (allDocuments || []).filter(
    (doc) =>
      !linkedIds.has(doc.id) &&
      (doc.title.toLowerCase().includes(search.toLowerCase()) ||
        (doc.folder_name || '').toLowerCase().includes(search.toLowerCase()))
  );

  const handleLink = (documentId: string) => {
    linkDocument.mutate({ deliverableId, documentId }, {
      onSuccess: () => {
        setShowPicker(false);
        setSearch('');
      },
    });
  };

  const handleUnlink = (doc: LinkedDocument) => {
    unlinkDocument.mutate({ deliverableId, documentId: doc.id });
  };

  const handleCreateAndLink = async () => {
    const url = newLinkUrl.trim();
    if (!url) return;
    const title = newLinkTitle.trim() || url;
    try {
      const result = await createLinkDocument.mutateAsync({ title, external_url: url });
      const docId = result?.document?.id || result?.id;
      if (docId) {
        await linkDocument.mutateAsync({ deliverableId, documentId: docId });
      }
      setNewLinkUrl('');
      setNewLinkTitle('');
      setShowNewLink(false);
    } catch {
      // errors handled by hook toasts
    }
  };

  if (isLoading) {
    return (
      <div className="px-3 py-2 text-xs text-muted-foreground">
        Loading linked documents...
      </div>
    );
  }

  return (
    <div className="px-3 py-2 border-t bg-blue-50/30 space-y-1">
      {/* Section header */}
      <div className="flex items-center gap-1.5 pb-0.5">
        <FileText className="h-3 w-3 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground">Documents</span>
        <span className="text-[10px] text-muted-foreground/70">— Link from your document library</span>
      </div>

      {/* Linked documents list */}
      {(linkedDocs || []).map((doc) => (
        <div key={doc.id} className="flex items-center gap-2 py-1 group">
          {doc.document_type === 'link' ? (
            <Link2 className="h-3.5 w-3.5 text-blue-500 shrink-0" />
          ) : (
            <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          )}
          <span className="text-xs font-medium truncate flex-1">{doc.title}</span>
          {doc.folder_name && (
            <span className="text-[10px] text-muted-foreground flex items-center gap-0.5 whitespace-nowrap">
              <FolderOpen className="h-2.5 w-2.5" />
              {doc.folder_name}
            </span>
          )}
          {doc.current_version > 1 && (
            <span className="text-[10px] text-muted-foreground whitespace-nowrap">
              v{doc.current_version}
            </span>
          )}
          <span className="text-[10px] text-muted-foreground whitespace-nowrap">
            {timeAgo(doc.linked_at)}
          </span>
          {doc.document_type === 'link' && doc.external_url && (
            <a
              href={doc.external_url}
              target="_blank"
              rel="noopener noreferrer"
              className="h-5 w-5 inline-flex items-center justify-center opacity-0 group-hover:opacity-100 text-blue-500 hover:text-blue-700"
              title="Open link"
            >
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5 opacity-0 group-hover:opacity-100"
            onClick={() => handleUnlink(doc)}
            title="Unlink document"
          >
            <Unlink className="h-3 w-3 text-destructive" />
          </Button>
        </div>
      ))}

      {/* Empty state */}
      {(!linkedDocs || linkedDocs.length === 0) && !showPicker && (
        <div className="text-center py-2">
          <p className="text-[11px] text-muted-foreground">Link documents from your library</p>
        </div>
      )}

      {/* Document picker */}
      {showPicker && (
        <div className="border rounded-md bg-white p-2 space-y-1.5 shadow-sm">
          <div className="flex items-center gap-1.5">
            <Search className="h-3 w-3 text-muted-foreground shrink-0" />
            <Input
              placeholder="Search documents..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-6 text-xs"
              autoFocus
            />
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0"
              onClick={() => { setShowPicker(false); setSearch(''); }}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
          <div className="max-h-[160px] overflow-y-auto space-y-0.5">
            {availableDocs.length === 0 && (
              <p className="text-[11px] text-muted-foreground text-center py-2">
                {search ? 'No matching documents' : 'All documents already linked'}
              </p>
            )}
            {availableDocs.slice(0, 20).map((doc) => (
              <button
                key={doc.id}
                className="w-full flex items-center gap-2 px-2 py-1 rounded text-left hover:bg-accent/50 transition-colors"
                onClick={() => handleLink(doc.id)}
                disabled={linkDocument.isPending}
              >
                {doc.document_type === 'link' ? (
                  <Link2 className="h-3 w-3 text-blue-500 shrink-0" />
                ) : (
                  <FileText className="h-3 w-3 text-muted-foreground shrink-0" />
                )}
                <span className="text-xs truncate flex-1">{doc.title}</span>
                {doc.folder_name && (
                  <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                    {doc.folder_name}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* New link inline form */}
      {showNewLink && (
        <div className="border rounded-md bg-white p-2 space-y-1.5 shadow-sm">
          <div className="flex items-center gap-1.5">
            <Globe className="h-3 w-3 text-blue-500 shrink-0" />
            <Input
              placeholder="https://..."
              value={newLinkUrl}
              onChange={(e) => setNewLinkUrl(e.target.value)}
              className="h-6 text-xs flex-1"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateAndLink();
                if (e.key === 'Escape') { setShowNewLink(false); setNewLinkUrl(''); setNewLinkTitle(''); }
              }}
            />
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0"
              onClick={() => { setShowNewLink(false); setNewLinkUrl(''); setNewLinkTitle(''); }}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
          <div className="flex items-center gap-1.5">
            <Input
              placeholder="Title (optional — defaults to URL)"
              value={newLinkTitle}
              onChange={(e) => setNewLinkTitle(e.target.value)}
              className="h-6 text-xs flex-1"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateAndLink();
                if (e.key === 'Escape') { setShowNewLink(false); setNewLinkUrl(''); setNewLinkTitle(''); }
              }}
            />
            <Button
              size="sm"
              variant="default"
              className="h-6 text-xs px-2"
              onClick={handleCreateAndLink}
              disabled={!newLinkUrl.trim() || createLinkDocument.isPending || linkDocument.isPending}
            >
              {createLinkDocument.isPending || linkDocument.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                'Add'
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex items-center gap-2 pt-1">
        <Button
          variant="ghost"
          size="sm"
          className="h-6 text-xs text-muted-foreground"
          onClick={() => { setShowPicker(!showPicker); setShowNewLink(false); }}
        >
          <Plus className="h-3 w-3 mr-1" />
          Link document
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 text-xs text-blue-600 hover:text-blue-700"
          onClick={() => { setShowNewLink(!showNewLink); setShowPicker(false); }}
        >
          <Globe className="h-3 w-3 mr-1" />
          Add link
        </Button>
      </div>
    </div>
  );
}
